/**
 * Where the run snapshot is kept, and the only code that touches that key.
 *
 * Its own localStorage entry rather than a field on the save blob. Three
 * reasons, in order of how much they matter:
 *
 *  1. A bad snapshot must not be able to take the settings with it. They are
 *     parsed together or not at all, and this one is rewritten every few
 *     seconds while the other is written when somebody drags a slider.
 *  2. `SAVE_VERSION` can then stay where it is. Bumping it wipes settings and
 *     the lifetime record; the shape of a run in progress will move far more
 *     often than either, and it should be free to.
 *  3. The title screen reads the save blob on every `enter()` to draw the
 *     record line. It has no business parsing a run it is not going to load.
 *
 * Every path here swallows its own failures. localStorage is unavailable in
 * some private modes and full in others, and neither is a reason for the game
 * to stop working — the worst case is that resuming quietly stops being offered.
 */

import { decodeSnapshot, encodeSnapshot, type RunSnapshot } from './snapshot';

/**
 * Sibling of `SAVE_KEY` in `core/config.ts`. Declared here rather than there
 * because config is the tuning file and this is a storage detail; the `.v1`
 * suffix is a second line of defence behind `SNAPSHOT_VERSION`, for the day the
 * format changes so completely that even reading the old blob is a waste.
 */
const RUN_KEY = 'backrooms.run.v1';

/** The snapshot on disk, and the three things anyone does to it. */
export interface RunStore {
  /**
   * The snapshot as of the last read or write, without touching storage. Null
   * when there is nothing to go back to.
   */
  readonly current: RunSnapshot | null;
  /** Re-read from storage. Called once at boot; another tab is not our problem. */
  reload(): RunSnapshot | null;
  write(snapshot: RunSnapshot): void;
  /** Forget the run. Death and quit-to-title both mean this. */
  clear(): void;
  /**
   * Bytes the last successful write occupied, or 0. Only used for measuring —
   * localStorage is about 5 MB and shared with the settings, so it is worth
   * being able to answer how much of it this takes.
   */
  readonly bytes: number;
}

class Store implements RunStore {
  current: RunSnapshot | null = null;
  bytes = 0;

  constructor() {
    this.reload();
  }

  reload(): RunSnapshot | null {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(RUN_KEY);
    } catch {
      raw = null;
    }
    this.current = decodeSnapshot(raw);
    // A blob that failed validation is dead weight and will fail again on every
    // boot. Drop it now so the key does not sit there forever.
    if (raw !== null && this.current === null) this.clear();
    return this.current;
  }

  write(snapshot: RunSnapshot): void {
    const text = encodeSnapshot(snapshot);
    try {
      localStorage.setItem(RUN_KEY, text);
      this.current = snapshot;
      this.bytes = text.length;
    } catch {
      // Quota or private mode. The run carries on; it just will not survive the
      // tab being closed, which is exactly where the game was before this file.
    }
  }

  clear(): void {
    this.current = null;
    this.bytes = 0;
    try {
      localStorage.removeItem(RUN_KEY);
    } catch {
      /* nothing to do about it */
    }
  }
}

export function makeRunStore(): RunStore {
  return new Store();
}
